import React, { useContext } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type HeaderTitleContextValue = {
  content: React.ReactNode | null;
  setContent: (c: React.ReactNode | null) => void;
};
export const HeaderTitleContext = React.createContext<HeaderTitleContextValue>({
  content: null,
  setContent: () => {},
});

export function HeaderTitle({
  children,
}: {
  children: React.ReactNode | null;
}) {
  const { setContent } = React.useContext(HeaderTitleContext);
  React.useEffect(() => {
    setContent(children);
  }, [children, setContent]);
  return null;
}

export function HeaderIconTitle({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactElement<any> | null;
}) {
  const theme = useTheme();
  const Icon: any = icon;
  return (
    <ListItem
      component="div"
      css={{
        width: 'auto',
        color: theme.palette.grey[600],
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      }}
    >
      <ListItemIcon css={{ minWidth: 32 }}>{Icon}</ListItemIcon>
      <ListItemText primary={title} />
    </ListItem>
  );
}

export function HeaderTitleRenderer() {
  const { content } = React.useContext(HeaderTitleContext);

  return <>{content}</>;
}

export function HeaderTitleContextContainer(props: {
  children?: React.ReactNode;
}) {
  const [headerTitleContent, setHeaderTitleContent] =
    React.useState<React.ReactNode | null>(null);

  return (
    <HeaderTitleContext.Provider
      value={{
        content: headerTitleContent,
        setContent: setHeaderTitleContent,
      }}
    >
      {props?.children}
    </HeaderTitleContext.Provider>
  );
}

export enum ChatQueryPrams {
  openChatWithUserID = 'openChatWith',
}
export enum ChatVariant {
  double = 'double',
  single = 'single',
  wrap = 'wrap',
}
export enum ChatMessageSize {
  small = 'small',
  large = 'large',
}
export enum ChatPosition {
  right = 'right',
  left = 'left',
}

export type ChatData = {
  userID: string | null;
  appoID?: string;
  apscID?: string;
  megrID?: string;
  variant?: ChatVariant;
  messageSize?: ChatMessageSize;
  chatPosition?: ChatPosition;
  classes?: {
    chatWindow?: string;
  };
  onPressActions?: () => void;
};

export type AppointmentData = {
  appoID: string;
  patiID: string;
  section?: 'doctorNote';
};

export type ModulesEnabled = {
  survey: boolean;
  security: boolean;
  operations: boolean;
  planning: boolean;
};

export type UIContextValue = {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  modules: ModulesEnabled;
};

export const UIContext = React.createContext<UIContextValue>({
  sidebarOpen: true,
  setSidebarOpen: () => {},
  modules: {
    survey: true,
    security: true,
    operations: true,
    planning: true,
  },
});

export function UIContextContainer(props: { children?: React.ReactNode }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(isLg);

  const modules = React.useMemo(
    () => ({
      survey: import.meta.env.VITE_DISABLE_SURVEY_MODULE !== 'true',
      security: import.meta.env.VITE_DISABLE_SECURITY_MODULE !== 'true',
      operations: import.meta.env.VITE_DISABLE_OPERATIONS_MODULE !== 'true',
      planning: import.meta.env.VITE_DISABLE_PLANNING_MODULE !== 'true',
    }),
    []
  );

  React.useEffect(() => {
    if (!isLg) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isLg]);

  return (
    <UIContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        modules,
      }}
    >
      {props?.children}
    </UIContext.Provider>
  );
}

export const useUIContext = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIContextContainer');
  }
  return context;
};
